import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { EndPoints } from "../../Constants/endpoints";
import { useToast } from "../../Services/ToastContext";
import useEditPrescription from "../../api/Prescription/useEditPrescription";
 import { PrescriptionDType } from "../../interface/GeneralResponse";
 import { cleanFormData } from "./hooks/usePrescriptionSubmit";
import { useGetData } from "../../hooks";
import { PrescriptionBaseForm } from "./components";

const EditPrescription: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const { prescriptionId } = useParams();
  const [localInitialData, setLocalInitialData] = useState<any>(undefined);

  const { data: prescriptionData } = useGetData<PrescriptionDType>(
    EndPoints.GetPrescriptions,
    {},
    prescriptionId,
  );

  const { mutate, isPending } = useEditPrescription();
  const prescription = prescriptionData?.Data?.Prescription;

  useEffect(() => {
    if (prescription) {
      const drugsData =
        prescription.Drugs?.map((drug: any) => {
          if (!drug.PackageId && drug.Name) {
            return {
              Name: drug.Name,
              DrugName: drug.DrugName,
              Frequency: drug.FrequencyCode || "",
              SelectedDays: drug.Days
              ? drug.Days.map((day: any) => day.Id)
              : [],
              Meal: drug.MealCode || "",
              Sleep: drug.Sleep || false,
              Times: drug.Times,
              Duration: drug.Duration,
              Instructions: drug.Instructions || "",
              DosageNum: drug.DosageNum,
              DosageUnit: drug.DosageUnit,
            };
          } else {
            return {
              PackageId: drug.PackageId,
              DrugName: drug.DrugName,
              Frequency: drug.FrequencyCode || "",
              SelectedDays: drug.Days
                ? drug.Days.map((day: any) => day.Id)
                : [],
              Meal: drug.MealCode || "",
              Sleep: drug.Sleep || false,
              Times: drug.Times,
              Duration: drug.Duration,
              Instructions: drug.Instructions || "",
              DosageNum: drug.DosageNum,
              DosageUnit: drug.DosageUnit,
            };
          }
        }) || [];

      setLocalInitialData({
        Case: prescription.Case || "",
        Drugs: drugsData,
      }); 
    }
  }, [prescription]);

  const handleSubmit = (data: any) => {
    const cleanedData = cleanFormData(data);

    mutate(
      { id: Number(prescriptionId), data: cleanedData },
      {
        onSuccess: () => {
          showToast(t("prescription_updated_successfully"), "success");
          queryClient.refetchQueries({
            queryKey: [EndPoints.GetPrescriptions],
          });
          queryClient.refetchQueries({
            queryKey: [`${EndPoints.GetPrescriptions}/${prescriptionId}`],
          });
          navigate(`/my_prescriptions`);
        },
        onError: (error: any) => {
          showToast(error.message || t("error_updating_prescription"), "error");
        },
      },
    );
  };

  return (
    <PrescriptionBaseForm
      key={localInitialData?.Drugs?.length}
      initialData={localInitialData}
      onSubmit={handleSubmit}
      config={{
        title: t("edit_prescription"),
        submitButtonText: t("save"),
        caseLabel: t("template_name"),
        showPatientInfo: false,
      }}
      isLoading={isPending}
    />
  );
};

export default EditPrescription;
